import { ElementType, Fragment, ReactNode, useMemo } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { differenceInYears } from 'date-fns';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import type { ThreadHeaderSize } from '@frontend/thread-header-context';
import { theme } from '@frontend/theme';
import { Chip, PolymorphicComponentPropWithoutRef, Text } from '@frontend/design-system';

const EMPTY_BIRTHDATE = '0001-01-01T00:00:00Z';

const getStatusLabel = (status: PersonStatus_Enum, t: (key: string) => string): string | null => {
  switch (status) {
    case PersonStatus_Enum.ACTIVE:
      return t('Active');
    case PersonStatus_Enum.ARCHIVED:
      return t('Archived');
    case PersonStatus_Enum.INACTIVE:
      return t('Inactive');
    case PersonStatus_Enum.DECEASED:
      return t('Deceased');
    case PersonStatus_Enum.NON_PATIENT:
      return t('Non-Patient');
    case PersonStatus_Enum.PROSPECTIVE:
      return t('Prospective');
    default:
      return null;
  }
};

export type PersonSecondaryInfoProps<E extends ElementType = 'span'> = PolymorphicComponentPropWithoutRef<
  E,
  {
    personId?: string;
    person?: Person;
    groupIds?: string[];
    size?: ThreadHeaderSize;
    isCompactMode?: boolean;
    additionalInfo?: string | ReactNode;
    isLead?: boolean;
  }
>;

/**
 * `PersonSecondaryInfo` is a component that displays secondary information about a person.
 * The information displayed includes the person's sex, age, and status.
 *
 * @param personId - The ID of the person to display information for.
 * @param person (optional) - The person to display information for. If provided, this will be used instead of fetching
 * the person.
 * @param groupIds (optional) - The IDs of the groups to which the person belongs. If provided, this will be used to
 * fetch the person. If not provided, the component will use the currently selected location IDs.
 */
export const PersonSecondaryInfo = <E extends ElementType = 'span'>({
  as,
  personId,
  person: providedPerson,
  groupIds,
  size = 'normal',
  isCompactMode,
  additionalInfo,
  isLead,
  children,
  ...rest
}: PersonSecondaryInfoProps<E>) => {
  const { t } = useTranslation('thread-header');
  const { selectedLocationIds } = useAppScopeStore();
  const Component = as || 'span';
  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const { data: fetchedPerson } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: personId!,
      locationIds: groupIds?.length ? groupIds : selectedLocationIds,
    },
    {
      enabled: !providedPerson && !!personId && (!!groupIds?.length || !!selectedLocationIds.length),
    }
  );
  const person = providedPerson || fetchedPerson;
  const isLeadContact = isLead || (!personId && !!(person?.firstName || person?.lastName));

  const infoItems = useMemo(() => {
    const results: ReactNode[] = [];

    if (isLeadContact) {
      results.push(
        <Chip key='lead-contact' variant='tangerine' size='tiny'>
          {t('Lead')}
        </Chip>
      );
    } else {
      if (!isSmallMode && person?.gender) {
        switch (person.gender.toLowerCase()) {
          case 'male':
            results.push(t('M'));
            break;
          case 'female':
            results.push(t('F'));
            break;
          case 'nonbinary':
            results.push(t('NB'));
            break;
          case 'other':
            results.push(t('O'));
            break;
        }
      }
      if (!isSmallMode && person?.birthdate && person.birthdate > EMPTY_BIRTHDATE) {
        const age = differenceInYears(new Date(), new Date(person.birthdate));
        if (age > 0) {
          results.push(`${age}`);
        }
      }
      if (person?.status !== undefined && !isCompactMode) {
        const statusLabel = getStatusLabel(person.status, t);
        if (statusLabel) {
          results.push(
            <Chip variant='success' key='person-status' size='tiny'>
              {statusLabel}
            </Chip>
          );
        }
      }
    }

    if (additionalInfo) {
      results.push(additionalInfo);
    }

    return results;
  }, [isLeadContact, isSmallMode, person?.gender, person?.birthdate, person?.status, isCompactMode, additionalInfo, t]);

  const elementsWithDividers = infoItems.reduce<ReactNode[]>((acc, item, index) => {
    if (index !== 0) {
      acc.push(
        <Text key={`person-secondary-info-divider-${index}`} size='small' color='disabled'>
          •
        </Text>
      );
    }

    if (typeof item === 'string') {
      acc.push(
        <Text
          key={`person-secondary-info-item-${index}`}
          size={size === 'normal' ? 'medium' : 'small'}
          css={{ color: theme.colors.neutral70, overflowX: 'hidden', textOverflow: 'ellipsis' }}
        >
          {item}
        </Text>
      );
    } else {
      acc.push(<Fragment key={`person-secondary-info-node-${index}`}>{item}</Fragment>);
    }
    return acc;
  }, []);

  if (infoItems.length === 0 && !children) {
    return null;
  }

  return (
    <Component
      css={{
        display: 'flex',
        gap: theme.spacing(0.5),
        alignItems: 'center',
        lineHeight: 1,
        textOverflow: 'ellipsis',
      }}
      {...rest}
    >
      {elementsWithDividers}
      {children}
    </Component>
  );
};
